import { Form, Row, Col, Input, Button } from "antd";
import { MinusCircleOutlined } from "@ant-design/icons";
import { createGenericIntervention } from './helpers'
import RecordQuarterSelect from "./RecordQuarterSelect";

const getPropertyNode = async (quarterId) => {
  const res = await new Weaver.Sparql(`
    PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
    PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
    PREFIX xsd: <http://www.w3.org/2001/XMLSchema#>
    PREFIX cti: <http://pms.coentunnelcompany.nl/instance#>
    PREFIX ct: <http://pms.coentunnelcompany.nl/rdl#>
    SELECT ?bbv
    WHERE {
      VALUES ?bbvq { <${quarterId}> }
      ?bbv          rdf:type           ct:BrutoBeschikbaarheidsvergoeding .
      ?bbvq         ct:hasProperty     ?bbv
    }
  `).find();
  return res.bbv[0];
}

const getWaarde = async (typeName) => {
  const res = await new Weaver.Sparql(`
    PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
    PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
    PREFIX xsd: <http://www.w3.org/2001/XMLSchema#>
    PREFIX cti: <http://pms.coentunnelcompany.nl/instance#>
    PREFIX ct: <http://pms.coentunnelcompany.nl/rdl#>
    SELECT ?waarde
    WHERE {
      ?waarde          rdf:type           ct:${typeName} .
    }
  `).find();
  return await res.waarde[0].load();
}

export const BbvRecordList = ({ active, quarter, options, form, rowIndex }) => {
  const colStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center' }
  return(
    <Form.List name={[rowIndex, 'records']}>
      {(fields, { add, remove }) => {
        return (
          <div key={`records-${rowIndex}`}>
            {
              fields.map((field, index) => {
                return(
                  <Form.Item
                    key={field.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Row gutter={8} type="flex">
                      <Col style={{ flex: 1, ...colStyle }}>
                        Kwartaal
                      </Col>
                      <Col style={{ flex: 5, ...colStyle}}>
                        <Form.Item
                          noStyle
                          name={[field.name, 'bbvq']}
                        >
                          <RecordQuarterSelect
                            rowIndex={rowIndex}
                            field={field}
                            quarter={quarter}
                            form={form}
                            style={{ width: '100%' }}
                            placeholder='Select a quarter'
                            disabled={rowIndex !== active}
                            options={options}
                          />
                        </Form.Item>
                      </Col>
                      <Col style={{ flex: 2, ...colStyle }}>
                        verander BBV
                      </Col>
                      <Col style={{ flex: 5 }}>
                        <Form.Item
                          noStyle
                          name={[field.name, 'oldValue']}
                        >
                          <Input disabled={true}/>
                        </Form.Item>
                      </Col>
                      <Col style={{ flex: 1, ...colStyle }}>
                        in
                      </Col>
                      <Col style={{ flex: 5, ...colStyle }}>
                        <Form.Item
                          noStyle
                          name={[field.name, 'newValue']}
                        >
                          <Input disabled={rowIndex !== active}/>
                        </Form.Item>
                      </Col>
                      <Col style={{ flex: 1, ...colStyle }}>
                        <Form.Item noStyle>
                          <Button
                            type="text"
                            icon={<MinusCircleOutlined style={{ color: '#eb3c3c' }}/>}
                            onClick={async () => {
                              let formVals = form.getFieldsValue()
                              const toRemove = formVals.interventions[active]?.records[field.name].bbvq
                              const remaining = formVals.interventions[active].records.map(({ bbvq }) => bbvq).filter((_bbvq) => toRemove !== _bbvq)
                              formVals.interventions[active].qname = remaining
                              form.setFieldsValue(formVals)
                              remove(field.name)
                            }}
                            disabled={rowIndex !== active}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form.Item>
                )
              })
            }
            {
              rowIndex === active && (
                <Form.Item>
                  <Button
                    onClick={() => add({ bbvq: [] })}
                    type="dashed"
                    block
                  >
                    Add Record
                  </Button>
                </Form.Item>
              )
            }
          </div>
        )}
      }
    </Form.List>
  )
}

export const createBbvIntervention = async (existingData, newData) => {
  const writes = [];

  // Section records
  const addRecord = async (intervention, newRecord, writes) => {
    const record = new Weaver.Node();
    newRecord.record = record;
    const propertyNode = await getPropertyNode(newRecord.bbvq);
    record.relation('http://pms.coentunnelcompany.nl/rdl#oldSubject').add(propertyNode);
    record.relation('http://pms.coentunnelcompany.nl/rdl#newSubject').add(propertyNode);
    record.relation('http://pms.coentunnelcompany.nl/rdl#oldPredicate').add(Weaver.Node.get('http://pms.coentunnelcompany.nl/rdl#hasValue'));
    record.relation('http://pms.coentunnelcompany.nl/rdl#newPredicate').add(Weaver.Node.get('http://pms.coentunnelcompany.nl/rdl#hasValue'));
    record.set('http://pms.coentunnelcompany.nl/rdl#oldObject', newRecord.oldValue);
    record.set('http://pms.coentunnelcompany.nl/rdl#newObject', newRecord.newValue);
    Weaver.Functions.collectWrites(record, writes);
    intervention.relation('http://pms.coentunnelcompany.nl/rdl#appliesTo').add(record);
    Weaver.Functions.popWrites(intervention.relation('http://pms.coentunnelcompany.nl/rdl#appliesTo'), writes);
  }

  const updateRecord = async (oldRecord, newRecord, writes) => {
    const record = oldRecord.record;
    await record.load();
    if (oldRecord.bbvq !== newRecord.bbvq) {
      const existingPropertyNode = record.relation('http://pms.coentunnelcompany.nl/rdl#oldSubject').first();
      const propertyNode = await getPropertyNode(newRecord.bbvq);
      record.relation('http://pms.coentunnelcompany.nl/rdl#oldSubject').update(existingPropertyNode, propertyNode);
      record.relation('http://pms.coentunnelcompany.nl/rdl#newSubject').update(existingPropertyNode, propertyNode);
      Weaver.Functions.collectWrites(record, writes);
    }
    if (oldRecord.newValue !== newRecord.newValue) {
      record.set('http://pms.coentunnelcompany.nl/rdl#newObject', newRecord.newValue);
      Weaver.Functions.popWrites(record.attribute('http://pms.coentunnelcompany.nl/rdl#newObject'), writes);
    }
  }

  return await createGenericIntervention(existingData, newData, addRecord, updateRecord, writes);
}

export const mapBbvIntervention = (valueHolderKey) => (intv) => ({
  intervention: intv?.value,
  name: intv.children?.name[0]?.value?.value,
  reason: intv.children?.reason[0]?.value?.value,
  docid: intv.children?.docid[0]?.value?.value,
  qname: intv.children?.qid.map((n) => n?.value?.id()),
  records: intv.children?.record.map((record) => ({
    record: record?.value,
    [valueHolderKey]: record.children?.[valueHolderKey][0]?.value?.nodeId,
    oldValue: record.children?.oldValue[0]?.value?.value,
    newValue: record.children?.newValue[0]?.value?.value
  }))
})

export const WaardeDagNachtRecordList = (typeName) => ({ active, options, form, rowIndex }) => {
  const colStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center' }
  return(
    <Form.List name={[rowIndex, 'records']}>
      {(fields, { add, remove }) => {
        return (
          <div key={`records-${rowIndex}`}>
            {
              fields.map((field, index) => {
                return(
                  <Form.Item
                    key={field.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Row gutter={8} type="flex">
                      <Col style={{ flex: 2, ...colStyle }}>
                        verander
                      </Col>
                      <Col style={{ flex: 5 }}>
                        <Form.Item
                          noStyle
                          name={[field.name, 'oldValue']}
                        >
                          <Input disabled={true}/>
                        </Form.Item>
                      </Col>
                      <Col style={{ flex: 1, ...colStyle }}>
                        in
                      </Col>
                      <Col style={{ flex: 5, ...colStyle }}>
                        <Form.Item
                          noStyle
                          name={[field.name, 'newValue']}
                        >
                          <Input disabled={rowIndex !== active}/>
                        </Form.Item>
                      </Col>
                      <Col style={{ flex: 1, ...colStyle }}>
                        <Form.Item noStyle>
                          <Button
                            type="text"
                            icon={<MinusCircleOutlined style={{ color: '#eb3c3c' }}/>}
                            onClick={async () => {
                              let formVals = form.getFieldsValue()
                              const toRemove = formVals.interventions[active]?.records[field.name].waarde
                              const remaining = formVals.interventions[active].records.map(({ waarde }) => waarde).filter((_waarde) => toRemove !== _waarde)
                              formVals.interventions[active].waarde = remaining
                              form.setFieldsValue(formVals)
                              remove(field.name)
                            }}
                            disabled={rowIndex !== active}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form.Item>
                )
              })
            }
            {
              rowIndex === active && (
                <Form.Item>
                  <Button
                    onClick={async () => {
                      const waardeNode = await getWaarde(typeName);
                      const oldValue = waardeNode.attribute('http://pms.coentunnelcompany.nl/rdl#hasValue').allRecords()[0].value;
                      add({ waarde: waardeNode, oldValue })
                    }}
                    type="dashed"
                    block
                  >
                    Add Record
                  </Button>
                </Form.Item>
              )
            }
          </div>
        )}
      }
    </Form.List>
  )
}

export const createWaardeDagNachtIntervention = async (existingData, newData) => {
  const writes = [];

  // Section records
  const addRecord = async (intervention, newRecord, writes) => {
    const record = new Weaver.Node();
    newRecord.record = record;
    record.relation('http://pms.coentunnelcompany.nl/rdl#oldSubject').add(newRecord.waarde);
    record.relation('http://pms.coentunnelcompany.nl/rdl#newSubject').add(newRecord.waarde);
    record.relation('http://pms.coentunnelcompany.nl/rdl#oldPredicate').add(Weaver.Node.get('http://pms.coentunnelcompany.nl/rdl#hasValue'));
    record.relation('http://pms.coentunnelcompany.nl/rdl#newPredicate').add(Weaver.Node.get('http://pms.coentunnelcompany.nl/rdl#hasValue'));
    record.set('http://pms.coentunnelcompany.nl/rdl#oldObject', newRecord.oldValue);
    record.set('http://pms.coentunnelcompany.nl/rdl#newObject', newRecord.newValue);
    Weaver.Functions.collectWrites(record, writes);
    intervention.relation('http://pms.coentunnelcompany.nl/rdl#appliesTo').add(record);
    Weaver.Functions.popWrites(intervention.relation('http://pms.coentunnelcompany.nl/rdl#appliesTo'), writes);
  }

  const updateRecord = async (oldRecord, newRecord, writes) => {
    const record = oldRecord.record;
    await record.load();
    if (oldRecord.waarde !== newRecord.waarde) {
      const existingWaarde = record.relation('http://pms.coentunnelcompany.nl/rdl#oldSubject').first();
      record.relation('http://pms.coentunnelcompany.nl/rdl#oldSubject').update(existingWaarde, newRecord.waarde);
      record.relation('http://pms.coentunnelcompany.nl/rdl#newSubject').update(existingWaarde, newRecord.waarde);
      Weaver.Functions.collectWrites(record, writes);
    }
    if (oldRecord.newValue !== newRecord.newValue) {
      record.set('http://pms.coentunnelcompany.nl/rdl#newObject', newRecord.newValue);
      Weaver.Functions.popWrites(record.attribute('http://pms.coentunnelcompany.nl/rdl#newObject'), writes);
    }
  }

  return await createGenericIntervention(existingData, newData, addRecord, updateRecord, writes);
}